import { getFlag } from '../../utils/selector'
import {
  SUPER_PROPERTY_PROFILE_ID,
  SUPER_PROPERTY_USER_ID,
  SUPER_PROPERTY_PLAN_TYPE,
  SUPER_PROPERTY_PLAN_ID,
  SUPER_PROPERTY_BILLING_FREQUENCY
} from '../../constants/amplitudeEvents'

/* eslint-disable no-unused-expressions */

/**
 * Amplitude Analytics Service
 *
 * User Properties: Persist across all events (set via identify/registerUserSupers/registerPlanSupers)
 * Event Properties: Specific to each event (passed to track())
 */
class Amplitude {
  constructor() {
    /*
    * If env === 'dev' || 'stage' || 'prod', always initiate amplitude library
    * If env === 'development', initiate amplitude library if amplitude feature flag is on
    * if env === 'test', never initiate amplitude library
    * */

    if ((process.env.NODE_ENV === 'development' && getFlag('amplitude')) || process.env.NODE_ENV !== 'test') {
      this.amplitude = require('@amplitude/analytics-browser')
      this.amplitude.init(process.env.REACT_APP_AMPLITUDE_KEY, {
        // Configure Amplitude options here
        defaultTracking: false, // Disable automatic tracking
        logLevel: this.amplitude.Types.LogLevel.Debug, // Enable debug logging in console
        minIdLength: 1, // Allow user IDs with minimum length of 1 character
      })
    }
    this.metadataReady = false
    this.supers = {}  // Store user-level super properties (persist across all events)
  }

  /**
       * Identify user and set user ID in Amplitude
       * @param {Object} user - User object with profile and id
       * @returns {Promise|undefined} Amplitude identify promise
       */
  identify(user = {}) {
    // form respondent or embed scenario
    if (user.profile && !user.id) {
      this.metadataReady = true
      return this.amplitude?.setUserId(`${user.profile}`)
    } else if (user.profile && user.id) {
      // A precaution to make sure user ID is set before tracking events
      // Tho it is not likely to return bad user data from api side
      this.metadataReady = true
      return this.amplitude?.setUserId(user.id)
    }
    return
  }

  /**
       * Set USER PROPERTIES (attributes that describe WHO the user IS)
       * These properties persist across ALL events for this user
       *
       * @param {Object} props - User properties to set
       * @returns {Promise|boolean} Amplitude identify promise or false if not ready
       */
  setUserProperty(props) {
    if (!this.amplitude || !this.metadataReady) return false

    const identify = new this.amplitude.Identify()
    Object.keys(props).forEach(key => {
      identify.set(key, props[key])
    })
    return this.amplitude.identify(identify)
  }

  /**
       * Register user super properties (profile id, user id)
       * These are USER PROPERTIES that persist across all events
       *
       * @param {Object} user - User object with profile and id
       */
  registerUserSupers(user) {
    const { profile, id } = user || {}
    this.supers = {
      ...this.supers,
      ...(profile ? { [SUPER_PROPERTY_PROFILE_ID]: profile } : {}),
      ...(id ? { [SUPER_PROPERTY_USER_ID]: id } : {}),
    }

    // Set user properties in Amplitude (persist across all events)
    if (this.amplitude && this.metadataReady) {
      const identify = new this.amplitude.Identify()
      Object.keys(this.supers).forEach(key => {
        identify.set(key, this.supers[key])
      })
      this.amplitude.identify(identify)
    }
  }

  /**
       * Register plan super properties (plan type, plan id, billing frequency)
       * These are USER PROPERTIES that persist across all events
       *
       * @param {Object} plan - Plan object with name, id, and billingInterval
       */
  registerPlanSupers(plan) {
    const { name, id, billingInterval } = plan || {}
    this.supers = {
      ...this.supers,
      ...(name ? { [SUPER_PROPERTY_PLAN_TYPE]: name } : {}),
      ...(id ? { [SUPER_PROPERTY_PLAN_ID]: id } : {}),
      ...(billingInterval ? { [SUPER_PROPERTY_BILLING_FREQUENCY]: billingInterval } : {}),
    }

    // Set user properties in Amplitude (persist across all events)
    if (this.amplitude && this.metadataReady) {
      const identify = new this.amplitude.Identify()
      Object.keys(this.supers).forEach(key => {
        identify.set(key, this.supers[key])
      })
      this.amplitude.identify(identify)
    }
  }

  /**
       * Track an event with EVENT PROPERTIES
       *
       * @param {string} name - Event name (use constants from amplitudeEvents.js)
       * @param {Object} eventProps - Event properties (data specific to this event)
       * @returns {Promise|undefined} Amplitude track promise
       */
  track(name, eventProps = {}) {
    if (!this.metadataReady || !this.amplitude) return

    // Amplitude Browser SDK: Track event with event_properties
    // User properties (super properties) are set separately via Identify API
    // and persist across all events automatically
    return this.amplitude.track(name, eventProps)
  }

  /**
       * Reset the Amplitude state (e.g., on logout)
       */
  reset() {
    this.metadataReady = false
    this.supers = {}
    return this.amplitude?.reset()
  }
}

export let amplitude = new Amplitude()

/* eslint-disable no-unused-expressions */

